import React, { useEffect, useState, useMemo } from 'react';
import {
  Stack,
  Text,
  Spinner,
  MessageBar,
  MessageBarType,
  DocumentCard,
  DocumentCardDetails,
  DocumentCardTitle,
  DocumentCardType,
  Icon,
  SearchBox,
  TooltipHost,
  Selection,
  SelectionMode,
  CommandBar,
  ICommandBarItemProps,
  Checkbox,
  IconButton,
  Dialog,
  DialogType,
  DialogFooter,
  PrimaryButton,
  DefaultButton,
} from '@fluentui/react';
import { useNavigate } from 'react-router-dom';
import { IBestellungMitPositionen, IUserPermissions } from '../../shared/models';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BADGE_COLORS = {
  gray:   { bg: '#d0d0d0', text: '#555' },
  green:  { bg: '#107c10', text: '#fff' },
  yellow: { bg: '#d4960a', text: '#fff' },
  red:    { bg: '#c50f1f', text: '#fff' },
};
function tileBadgeColor(total: number, unread: number, hasMention: boolean) {
  if (total === 0) return BADGE_COLORS.gray;
  if (unread === 0) return BADGE_COLORS.green;
  if (hasMention) return BADGE_COLORS.red;
  return BADGE_COLORS.yellow;
}
interface IArchivBadgeData {
  kommentare: { total: number; unread: number; hasMention: boolean };
}

const getGenehmigungsstatusColor = (status: string): string => {
  switch (status) {
    case 'Freigegeben':
      return '#107c10';
    case 'Teilweise freigegeben':
      return '#0078d4';
    case 'Offen':
      return '#ffaa44';
    case 'Abgelehnt':
      return '#d13438';
    case 's. Position':
      return '#0078d4';
    default:
      return '#605e5c';
  }
};

const getBestellstatusColor = (status: string): string => {
  switch (status) {
    case 'Geliefert':
      return '#107c10'; // Dunkelgrün
    case 'Teilweise geliefert':
      return '#5db85d'; // Hellgrün
    case 'Bestellt':
      return '#ffaa44'; // Orange
    case 'Eingereicht':
      return '#d13438'; // Rot
    case 's. Position':
      return '#0078d4'; // Blau
    default:
      return '#605e5c'; // Grau
  }
};

export const ArchivListe: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bestellungen, setBestellungen] = useState<IBestellungMitPositionen[]>([]);
  const [filteredBestellungen, setFilteredBestellungen] = useState<IBestellungMitPositionen[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [selectedItems, setSelectedItems] = useState<IBestellungMitPositionen[]>([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [permissions, setPermissions] = useState<IUserPermissions | null>(null);
  const [unarchiveDialogVisible, setUnarchiveDialogVisible] = useState(false);
  const [unarchiving, setUnarchiving] = useState(false);
  const [tileBadges, setTileBadges] = useState<Record<number, IArchivBadgeData>>({});

  useEffect(() => {
    loadBestellungen();
    loadPermissions();
  }, []);

  useEffect(() => {
    filterBestellungen();
  }, [searchText, bestellungen]);

  const loadPermissions = async () => {
    try {
      if (user?.email) {
        const response = await axios.get('/api/permissions/me', {
          params: { email: user.email }
        });
        setPermissions(response.data);
      }
    } catch (err: any) {
      console.error('Fehler beim Laden der Berechtigungen:', err?.response?.data || err.message || err);
    }
  };

  const loadBestellungen = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/api/bestellungen/archived');
      setBestellungen(response.data);
      const ids = (response.data as IBestellungMitPositionen[]).map(b => b.Id).filter(Boolean) as number[];
      if (ids.length) {
        axios.get(`/api/read-status/archiv-bestellungen?ids=${ids.join(',')}`)
          .then(r => setTileBadges(r.data))
          .catch(() => {});
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Fehler beim Laden der archivierten Bestellungen');
    } finally {
      setLoading(false);
    }
  };

  const filterBestellungen = () => {
    let filtered = [...bestellungen];

    // Suchfilter (erweitert um zusätzliche Felder)
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter((b) => {
        // Suche in Bestellungsfeldern
        const bestellungMatch =
          b.Title?.toLowerCase().includes(searchLower) ||
          b.InterneBestellnummer?.toLowerCase().includes(searchLower) ||
          b.AnforderndePersonen?.toLowerCase().includes(searchLower) ||
          b.AnforderndeKostenstelle?.toLowerCase().includes(searchLower) ||
          b.Personalnummer?.toLowerCase().includes(searchLower);

        // Suche in Positionsfeldern
        const positionMatch = b.Positionen?.some(
          (p) =>
            p.EmpfangendeKostenstelle?.toLowerCase().includes(searchLower) ||
            p.Artikelname?.toLowerCase().includes(searchLower) ||
            p.Artikelnummer?.toLowerCase().includes(searchLower) ||
            p.BCNummer?.toLowerCase().includes(searchLower) ||
            p.Sachkonto?.toLowerCase().includes(searchLower) ||
            p.InventarAnlagennummer?.toLowerCase().includes(searchLower)
        );

        return bestellungMatch || positionMatch;
      });
    }

    setFilteredBestellungen(filtered);
  };

  const handleUnarchiveSelected = async () => {
    if (selectedItems.length === 0) return;

    setUnarchiving(true);
    try {
      for (const bestellung of selectedItems) {
        if (bestellung.Id) {
          await axios.post(`/api/bestellungen/${bestellung.Id}/unarchive`);
        }
      }
      setUnarchiveDialogVisible(false);
      setSelectedItems([]);
      setSelectionMode(false);
      await loadBestellungen();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Fehler beim Dearchivieren');
    } finally {
      setUnarchiving(false);
    }
  };

  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    if (selectionMode) {
      setSelectedItems([]);
    }
  };

  const toggleSelection = (bestellung: IBestellungMitPositionen) => {
    if (selectedItems.some(item => item.Id === bestellung.Id)) {
      setSelectedItems(selectedItems.filter(item => item.Id !== bestellung.Id));
    } else {
      setSelectedItems([...selectedItems, bestellung]);
    }
  };

  const commandBarItems: ICommandBarItemProps[] = useMemo(() => {
    const items: ICommandBarItemProps[] = [];

    // "Zurück" Button
    items.push({
      key: 'back',
      text: 'Zurück',
      iconProps: { iconName: 'Back' },
      onClick: () => navigate('/bestellungen'),
    });

    // "Auswählen" Button (nur wenn Benutzer Berechtigung hat)
    if (permissions?.canArchive) {
      items.push({
        key: 'select',
        text: selectionMode ? 'Auswahl beenden' : 'Auswählen',
        iconProps: { iconName: selectionMode ? 'Cancel' : 'MultiSelect' },
        onClick: toggleSelectionMode,
      });
    }

    // "Dearchivieren" Button (nur wenn Elemente ausgewählt)
    if (selectionMode && selectedItems.length > 0 && permissions?.canArchive) {
      items.push({
        key: 'unarchive',
        text: `Dearchivieren (${selectedItems.length})`,
        iconProps: { iconName: 'Undo' },
        onClick: () => setUnarchiveDialogVisible(true),
      });
    }

    return items;
  }, [selectedItems, permissions, selectionMode, navigate]);

  if (loading) {
    return (
      <Stack verticalAlign="center" horizontalAlign="center" styles={{ root: { height: '100%', paddingTop: 100 } }}>
        <Spinner label="Lade archivierte Bestellungen..." />
      </Stack>
    );
  }

  return (
    <Stack tokens={{ childrenGap: 20 }}>
      {/* Header */}
      <Text variant="xxLarge" styles={{ root: { fontWeight: 600 } }}>
        Archiv
      </Text>

      {/* CommandBar mit allen Aktionen */}
      <CommandBar
        items={commandBarItems}
        styles={{
          root: {
            backgroundColor: '#f3f2f1',
            padding: 0,
          },
        }}
      />

      {/* Suchleiste */}
      <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center" wrap>
        <SearchBox
          placeholder="Suchen..."
          value={searchText}
          onChange={(_, newValue) => setSearchText(newValue || '')}
          styles={{ root: { width: 300 } }}
        />
      </Stack>

      {error && (
        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
          {error}
        </MessageBar>
      )}

      {/* Statistik */}
      <Stack horizontal tokens={{ childrenGap: 16 }}>
        <Stack
          styles={{
            root: {
              padding: '12px 16px',
              backgroundColor: 'white',
              borderLeft: '3px solid #605e5c',
              boxShadow: '0 1.6px 3.6px 0 rgba(0,0,0,.132), 0 0.3px 0.9px 0 rgba(0,0,0,.108)',
              minWidth: 120,
            },
          }}
        >
          <Text variant="large" styles={{ root: { fontWeight: 600, color: '#323130' } }}>
            {filteredBestellungen.length}
          </Text>
          <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
            Archiviert
          </Text>
        </Stack>
        {selectionMode && selectedItems.length > 0 && (
          <Stack
            styles={{
              root: {
                padding: '12px 16px',
                backgroundColor: '#deecf9',
                borderLeft: '3px solid #0078d4',
                boxShadow: '0 1.6px 3.6px 0 rgba(0,0,0,.132), 0 0.3px 0.9px 0 rgba(0,0,0,.108)',
                minWidth: 120,
              },
            }}
          >
            <Text variant="large" styles={{ root: { fontWeight: 600, color: '#0078d4' } }}>
              {selectedItems.length}
            </Text>
            <Text variant="small" styles={{ root: { color: '#0078d4' } }}>
              Ausgewählt
            </Text>
          </Stack>
        )}
      </Stack>

      {/* Kachelansicht */}
      {filteredBestellungen.length === 0 ? (
        <Stack horizontalAlign="center" tokens={{ padding: 40 }}>
          <Icon iconName="Archive" styles={{ root: { fontSize: 48, color: '#c8c6c4' } }} />
          <Text variant="large" styles={{ root: { marginTop: 16, color: '#605e5c' } }}>
            {searchText ? 'Keine Bestellungen gefunden' : 'Keine archivierten Bestellungen vorhanden'}
          </Text>
        </Stack>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
            gap: '16px',
          }}
        >
          {filteredBestellungen.map((bestellung) => {
            const selected = selectedItems.some(item => item.Id === bestellung.Id);
            const genehmigungsstatus = bestellung.Genehmigungsstatus || 'Offen';
            const bestellstatus = bestellung.Bestellstatus || 'Eingereicht';

            return (
              <DocumentCard
                key={bestellung.Id}
                type={DocumentCardType.compact}
                onClick={() => {
                  if (!selectionMode) {
                    navigate(`/archiv/${bestellung.Id}`);
                  }
                }}
                styles={{
                  root: {
                    cursor: 'pointer',
                    border: selected ? '2px solid #0078d4' : '1px solid #edebe9',
                    backgroundColor: selected ? '#f3f9fd' : 'white',
                    boxShadow: selected
                      ? '0 3.2px 7.2px 0 rgba(0,0,0,.132), 0 0.6px 1.8px 0 rgba(0,0,0,.108)'
                      : '0 1.6px 3.6px 0 rgba(0,0,0,.132), 0 0.3px 0.9px 0 rgba(0,0,0,.108)',
                    '&:hover': {
                      borderColor: '#0078d4',
                      boxShadow: '0 3.2px 7.2px 0 rgba(0,0,0,.132), 0 0.6px 1.8px 0 rgba(0,0,0,.108)',
                    },
                    transition: 'all 0.1s ease',
                    minHeight: '200px',
                    position: 'relative',
                  },
                }}
              >
                <DocumentCardDetails
                  styles={{
                    root: {
                      padding: '16px',
                    },
                  }}
                >
                  {/* Archive Icon (top left) */}
                  <div style={{ position: 'absolute', top: 8, left: 8 }}>
                    <IconButton
                      iconProps={{ iconName: 'Archive' }}
                      title="Archivierte Bestellung öffnen"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/archiv/${bestellung.Id}`);
                      }}
                      styles={{
                        root: {
                          width: 28,
                          height: 28,
                          color: '#605e5c',
                        },
                        rootHovered: {
                          color: '#0078d4',
                          backgroundColor: '#f3f2f1',
                        },
                      }}
                    />
                  </div>

                  {/* Badge-Indikatoren (Kommentare) – oben rechts */}
                  {bestellung.Id && (() => {
                    const badge = tileBadges[bestellung.Id!];
                    if (!badge) return null;
                    const kColor = tileBadgeColor(badge.kommentare.total, badge.kommentare.unread, badge.kommentare.hasMention);
                    return (
                      <div style={{
                        position: 'absolute',
                        top: 8,
                        right: selectionMode ? 36 : 8,
                        display: 'flex',
                        gap: 4,
                        alignItems: 'center',
                      }}>
                        <TooltipHost content={
                          badge.kommentare.total === 0 ? 'Keine Kommentare'
                          : badge.kommentare.unread === 0 ? `${badge.kommentare.total} Kommentar(e), alle gelesen`
                          : badge.kommentare.hasMention ? `${badge.kommentare.unread} ungelesen (inkl. @Erwähnung)`
                          : `${badge.kommentare.unread} Kommentar(e) ungelesen`
                        }>
                          <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            background: kColor.bg, color: kColor.text,
                            borderRadius: 10, padding: '1px 6px', fontSize: 11, fontWeight: 600,
                            cursor: 'default',
                          }} onClick={e => e.stopPropagation()}>
                            <Icon iconName="Comment" styles={{ root: { fontSize: 10 } }} />
                            {badge.kommentare.total}
                          </div>
                        </TooltipHost>
                      </div>
                    );
                  })()}

                  {/* Checkbox (top right) */}
                  {selectionMode && (
                    <div style={{ position: 'absolute', top: 8, right: 8 }}>
                      <Checkbox
                        checked={selected}
                        onChange={(e) => {
                          e?.stopPropagation();
                          toggleSelection(bestellung);
                        }}
                      />
                    </div>
                  )}

                  {/* Header mit Titel */}
                  <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} styles={{ root: { paddingLeft: 36, paddingRight: selectionMode ? 36 : 0 } }}>
                    <Text
                      variant="medium"
                      styles={{
                        root: {
                          fontWeight: 600,
                          color: '#323130',
                        },
                      }}
                    >
                      {bestellung.Title || 'Kein Titel'}
                    </Text>
                    {bestellung.Vertraulich && (
                      <TooltipHost content="Vertrauliche Bestellung">
                        <Icon iconName="Lock" styles={{ root: { color: '#d13438', fontSize: 14 } }} />
                      </TooltipHost>
                    )}
                  </Stack>

                  {/* Status mit Ampel */}
                  <Stack tokens={{ childrenGap: 4 }} styles={{ root: { paddingTop: '8px' } }}>
                    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                      <Text variant="small" styles={{ root: { color: '#605e5c', minWidth: '80px' } }}>
                        Genehmigt:
                      </Text>
                      <TooltipHost content={genehmigungsstatus}>
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            backgroundColor: getGenehmigungsstatusColor(genehmigungsstatus),
                            border: '1px solid #605e5c',
                          }}
                        />
                      </TooltipHost>
                    </Stack>
                    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                      <Text variant="small" styles={{ root: { color: '#605e5c', minWidth: '80px' } }}>
                        Status:
                      </Text>
                      <TooltipHost content={bestellstatus}>
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            backgroundColor: getBestellstatusColor(bestellstatus),
                            border: '1px solid #605e5c',
                          }}
                        />
                      </TooltipHost>
                    </Stack>
                  </Stack>

                  {/* Summe und Liefertermin */}
                  <Stack horizontal tokens={{ childrenGap: 16 }} styles={{ root: { paddingTop: '8px' } }}>
                    <Stack horizontal tokens={{ childrenGap: 6 }} verticalAlign="center">
                      <Icon iconName="Money" styles={{ root: { fontSize: 14, color: '#0078d4' } }} />
                      <Text
                        variant="medium"
                        styles={{
                          root: {
                            fontWeight: 600,
                            color: '#0078d4',
                          },
                        }}
                      >
                        {new Intl.NumberFormat('de-DE', {
                          style: 'currency',
                          currency: 'EUR',
                        }).format(bestellung.GesamtSumme || 0)}
                      </Text>
                    </Stack>
                  </Stack>

                  {/* Metadata */}
                  <Stack tokens={{ childrenGap: 6 }} styles={{ root: { paddingTop: '12px' } }}>
                    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                      <Icon iconName="Contact" styles={{ root: { fontSize: 14, color: '#605e5c' } }} />
                      <Text
                        variant="small"
                        styles={{
                          root: {
                            color: '#605e5c',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          },
                        }}
                      >
                        {bestellung.AnforderndePersonen || 'Unbekannt'}
                      </Text>
                    </Stack>

                    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                      <Icon iconName="Calendar" styles={{ root: { fontSize: 14, color: '#605e5c' } }} />
                      <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                        {bestellung.Erstellungsdatum
                          ? new Date(bestellung.Erstellungsdatum).toLocaleDateString('de-DE', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                            })
                          : 'Kein Datum'}
                      </Text>
                    </Stack>

                    {bestellung.AnforderndeKostenstelle && (
                      <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                        <Icon iconName="PaymentCard" styles={{ root: { fontSize: 14, color: '#605e5c' } }} />
                        <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                          KST: {bestellung.AnforderndeKostenstelle}
                        </Text>
                      </Stack>
                    )}
                  </Stack>
                </DocumentCardDetails>
              </DocumentCard>
            );
          })}
        </div>
      )}

      {/* Dearchivieren Dialog */}
      <Dialog
        hidden={!unarchiveDialogVisible}
        onDismiss={() => setUnarchiveDialogVisible(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Bestellungen dearchivieren',
          subText: `Möchten Sie ${selectedItems.length} Bestellung${selectedItems.length !== 1 ? 'en' : ''} wirklich dearchivieren? Die Bestellungen werden zurück in die aktive Liste verschoben.`,
        }}
      >
        <DialogFooter>
          <PrimaryButton onClick={handleUnarchiveSelected} text="Dearchivieren" disabled={unarchiving} />
          <DefaultButton onClick={() => setUnarchiveDialogVisible(false)} text="Abbrechen" disabled={unarchiving} />
        </DialogFooter>
      </Dialog>
    </Stack>
  );
};
